/**
 * Llamadas HTTP del dominio Pedidos de láminas.
 *
 * SIEMPRE sobre el cliente único `lib/api` (`docs/frontend_architecture.md`,
 * Pr. 2): ningún `fetch`/`ky` suelto. El cliente ya aporta `credentials:
 * 'include'`, el `X-CSRF-TOKEN` y el `X-Sede-Id` (sede activa) en cada request,
 * así que el tablero/historial/acciones operan sobre la sede activa sin tocar las
 * cabeceras a mano. Un `ApiError` (401/403/404/4xx/5xx) lo traduce el cliente y lo
 * gestionan los hooks/pantallas.
 *
 * Cubre la superficie de la API de dominio (`application/api/domains/
 * pedidos_lamina.py`): lectura (tablero, historial, detalle), alta única,
 * edición/borrado y las transiciones de estado (enviar/recibir/retirar-faltante).
 */
import { api } from "@/lib/api";

import type {
  PedidoCreateBody,
  PedidoDetail,
  PedidosBoard,
  PedidosHistorial,
  PedidoUpdateBody,
} from "./types";

/** Ruta base del dominio (relativa al prefijo `/api/v1` que pone `lib/api`). */
const BASE = "pedidos-laminas";

/**
 * Tablero de pedidos de la sede activa (`GET /api/v1/pedidos-laminas`).
 *
 * Permiso backend: `view_pedidos_laminas`. Devuelve las 5 listas del board
 * (pendientes de envío / en curso / en camino / recibidos recientes / faltantes)
 * más los metadatos de sede (origen/destino 39<->41).
 */
export async function fetchBoard(): Promise<PedidosBoard> {
  const response = await api.get(BASE);
  return (await response.json()) as PedidosBoard;
}

/**
 * Historial de recibidos de la sede activa (`GET /api/v1/pedidos-laminas/historial`).
 *
 * Permiso backend: `view_historial_pedidos_laminas`. Devuelve `{recibidos: [...]}`
 * en vista lista (del más reciente al más antiguo).
 */
export async function fetchHistorial(): Promise<PedidosHistorial> {
  const response = await api.get(`${BASE}/historial`);
  return (await response.json()) as PedidosHistorial;
}

/**
 * Detalle de un pedido (`GET /api/v1/pedidos-laminas/<id>`).
 *
 * Permiso backend: `view_pedidos_laminas`. Devuelve la vista detalle (superset de
 * la lista: descripción, flag de faltante, timestamps y autoría).
 */
export async function fetchPedido(id: number): Promise<PedidoDetail> {
  const response = await api.get(`${BASE}/${id}`);
  return (await response.json()) as PedidoDetail;
}

/**
 * Crea un pedido único (`POST /api/v1/pedidos-laminas`).
 *
 * Permiso backend: `add_pedidos_laminas`. El cuerpo es JSON; `cantidad` viaja como
 * texto (el service la trata como string de formulario). El backend valida sedes
 * 39<->41 y devuelve `{ok, message}` (201). Los reportes de faltante reusan este
 * endpoint con `es_faltante: true`.
 */
export async function createPedido(
  body: PedidoCreateBody,
): Promise<{ ok: boolean; message: string }> {
  const response = await api.post(BASE, { json: body });
  return (await response.json()) as { ok: boolean; message: string };
}

/**
 * Edita un pedido pendiente (`PATCH /api/v1/pedidos-laminas/<id>`).
 *
 * Permiso backend: `edit_pedidos_laminas` + admin (el service devuelve 403 si no
 * lo es). Devuelve el pedido actualizado en vista detalle (200).
 */
export async function updatePedido(
  id: number,
  body: PedidoUpdateBody,
): Promise<PedidoDetail> {
  const response = await api.patch(`${BASE}/${id}`, { json: body });
  return (await response.json()) as PedidoDetail;
}

/**
 * Elimina un pedido pendiente (`DELETE /api/v1/pedidos-laminas/<id>`).
 *
 * Permiso backend: `delete_pedidos_laminas` + admin. 204 sin cuerpo.
 */
export async function deletePedido(id: number): Promise<void> {
  await api.delete(`${BASE}/${id}`);
}

/**
 * Transición `pendiente → en_camino` (`POST /api/v1/pedidos-laminas/<id>/enviar`).
 *
 * Permiso backend: `send_pedidos_laminas`. Sin cuerpo. Devuelve el pedido
 * actualizado en vista detalle (200).
 */
export async function enviarPedido(id: number): Promise<PedidoDetail> {
  const response = await api.post(`${BASE}/${id}/enviar`);
  return (await response.json()) as PedidoDetail;
}

/**
 * Transición `en_camino → recibido` (`POST /api/v1/pedidos-laminas/<id>/recibir`).
 *
 * Permiso backend: `receive_pedidos_laminas`. Sin cuerpo. Suma a inventario v2 de
 * la sede destino (si hay referencia) y devuelve el pedido (detalle) más el flag
 * `inv_actualizado`.
 */
export async function recibirPedido(
  id: number,
): Promise<PedidoDetail & { inv_actualizado?: boolean }> {
  const response = await api.post(`${BASE}/${id}/recibir`);
  return (await response.json()) as PedidoDetail & { inv_actualizado?: boolean };
}

/**
 * Retira un aviso de faltante (`POST /api/v1/pedidos-laminas/<id>/retirar-faltante`).
 *
 * Permiso backend: `retirar_faltante_lamina`. 204 sin cuerpo.
 */
export async function retirarFaltante(id: number): Promise<void> {
  await api.post(`${BASE}/${id}/retirar-faltante`);
}
